/*
 * zh-podcast.js
 * Custom Audio-Player für die Magazin-Podcast-Section.
 * Steuert play/pause, Skip-Back-15s, Skip-Forward-30s, Progress-Bar,
 * Zeit-Anzeige und Speed-Cycling. Native <audio> bleibt unsichtbar im DOM,
 * wir steuern es per JS und spiegeln den State in die Custom-UI.
 */
(function () {
  'use strict';

  var SPEED_STEPS = [1, 1.25, 1.5, 1.75, 2, 0.75];

  function formatTime(sec) {
    if (!isFinite(sec) || sec < 0) return '0:00';
    var m = Math.floor(sec / 60);
    var s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  /* ---------------------------------------------------------------------
     PostHog listening telemetry

     The player is our own and the audio file sits on the Shopify Files CDN,
     so listening behaviour is observable here and nowhere else. We report
     three things: the first start, the milestones playback actually passes,
     and the end of an episode. `timeupdate` fires roughly 4x per second, so
     reporting it raw would mean thousands of events per listener - progress
     is therefore throttled to MILESTONES.
     --------------------------------------------------------------------- */
  var MILESTONES = [25, 50, 75, 95];

  /**
   * Sends one event, or stays silent when PostHog is unavailable (which is the
   * normal case without analytics consent).
   *
   * @param {string} name  - event name
   * @param {object} props - event properties
   */
  function capture(name, props) {
    var ph = window.posthog;
    if (!ph || typeof ph.capture !== 'function') return;
    try {
      ph.capture(name, props);
    } catch (e) {
      // Telemetry must never break playback.
    }
  }

  /**
   * Share of the episode played, or null while the metadata has not loaded
   * (duration NaN or 0).
   *
   * @param {HTMLAudioElement} audio
   * @returns {number|null} 0-100, unrounded
   */
  function percentPlayed(audio) {
    if (!audio.duration || !isFinite(audio.duration)) return null;
    return (audio.currentTime / audio.duration) * 100;
  }

  /**
   * Builds the property set every listening event carries.
   *
   * @param {HTMLElement} root       - player root holding the data attributes
   * @param {HTMLAudioElement} audio
   * @param {boolean} didSeek        - whether this listener has jumped at all
   * @param {object} [extra]         - event-specific properties
   * @returns {object}
   */
  function eventProps(root, audio, didSeek, extra) {
    var pct = percentPlayed(audio);
    var out = {
      player: root.getAttribute('data-zh-podcast') || 'unknown',
      article_handle: root.getAttribute('data-zh-podcast-article') || '',
      article_title: root.getAttribute('data-zh-podcast-title') || '',
      duration_seconds: isFinite(audio.duration) ? Math.round(audio.duration) : null,
      position_seconds: Math.round(audio.currentTime || 0),
      percent: pct === null ? null : Math.round(pct),
      did_seek: didSeek
    };
    if (extra) {
      Object.keys(extra).forEach(function (key) {
        out[key] = extra[key];
      });
    }
    return out;
  }

  /**
   * Marks every milestone at or below `pct` as used up WITHOUT reporting it.
   * Someone who drags to 90% has not listened to 25/50/75, and someone who
   * jumps back must not report the same milestone twice.
   *
   * @param {object} reached   - milestone bookkeeping, mutated in place
   * @param {number|null} pct
   */
  function consumeMilestones(reached, pct) {
    if (pct === null) return;
    MILESTONES.forEach(function (m) {
      if (pct >= m) reached[m] = true;
    });
  }

  /**
   * Attaches the listening telemetry to one player.
   *
   * @param {HTMLElement} root
   * @param {HTMLAudioElement} audio
   */
  function initTracking(root, audio) {
    var reached = Object.create(null);
    var didSeek = false;
    var started = false;
    var completed = false;

    function report(name, extra) {
      capture(name, eventProps(root, audio, didSeek, extra));
    }

    audio.addEventListener('play', function () {
      if (started) return;
      started = true;
      report('podcast_play');
    });

    // `seeking` fires synchronously on a currentTime assignment, so it lands
    // before the `timeupdate` that follows a skip button, a drag or an arrow key.
    audio.addEventListener('seeking', function () {
      didSeek = true;
      consumeMilestones(reached, percentPlayed(audio));
    });

    audio.addEventListener('timeupdate', function () {
      if (audio.seeking) return;
      var pct = percentPlayed(audio);
      if (pct === null) return;
      MILESTONES.forEach(function (m) {
        if (pct >= m && !reached[m]) {
          reached[m] = true;
          report('podcast_progress', { milestone: m });
        }
      });
    });

    audio.addEventListener('ended', function () {
      if (completed) return;
      completed = true;
      report('podcast_complete');
    });
  }

  function initPlayer(root) {
    // Two sections embed this file (zh-article-podcast, zh-magazine-home) and
    // neither section is limited to one instance, so the browser can execute
    // this IIFE more than once on a single page. Without this guard every
    // listener would be attached twice: play/pause would toggle against
    // itself and every telemetry event would be sent double.
    if (root.zhPodcastReady) return;
    root.zhPodcastReady = true;

    var audio = root.querySelector('[data-zh-podcast-audio]');
    var playBtn = root.querySelector('[data-zh-podcast-play]');
    var skipBtns = root.querySelectorAll('[data-zh-podcast-skip]');
    var speedBtn = root.querySelector('[data-zh-podcast-speed]');
    var speedLabel = root.querySelector('[data-zh-podcast-speed-label]');
    var progress = root.querySelector('[data-zh-podcast-progress]');
    var fill = root.querySelector('[data-zh-podcast-fill]');
    var current = root.querySelector('[data-zh-podcast-current]');
    var total = root.querySelector('[data-zh-podcast-total]');

    if (!audio || !playBtn) return;

    var speedIndex = 0;
    var isDragging = false;

    function setPlayingState(playing) {
      root.classList.toggle('is-playing', playing);
      playBtn.setAttribute('aria-label', playing ? 'Pausieren' : 'Abspielen');
      playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    }

    function updateProgress() {
      if (!audio.duration) return;
      var pct = (audio.currentTime / audio.duration) * 100;
      if (fill) fill.style.width = pct + '%';
      if (current) current.textContent = formatTime(audio.currentTime);
      if (progress) progress.setAttribute('aria-valuenow', Math.round(pct));
    }

    function updateDuration() {
      if (total) total.textContent = formatTime(audio.duration);
    }

    function seekFromEvent(clientX) {
      if (!audio.duration || !progress) return;
      var rect = progress.getBoundingClientRect();
      var ratio = (clientX - rect.left) / rect.width;
      ratio = Math.max(0, Math.min(1, ratio));
      audio.currentTime = ratio * audio.duration;
      updateProgress();
    }

    playBtn.addEventListener('click', function () {
      if (audio.paused) {
        var p = audio.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } else {
        audio.pause();
      }
    });

    audio.addEventListener('play', function () { setPlayingState(true); });
    audio.addEventListener('pause', function () { setPlayingState(false); });
    audio.addEventListener('ended', function () { setPlayingState(false); });
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);

    skipBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var offset = parseFloat(btn.getAttribute('data-zh-podcast-skip')) || 0;
        if (!audio.duration) return;
        audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + offset));
      });
    });

    if (speedBtn && speedLabel) {
      speedBtn.addEventListener('click', function () {
        speedIndex = (speedIndex + 1) % SPEED_STEPS.length;
        var rate = SPEED_STEPS[speedIndex];
        audio.playbackRate = rate;
        speedLabel.textContent = (rate % 1 === 0 ? rate.toFixed(0) : rate) + '×';
      });
    }

    if (progress) {
      progress.addEventListener('pointerdown', function (e) {
        isDragging = true;
        progress.setPointerCapture(e.pointerId);
        seekFromEvent(e.clientX);
      });
      progress.addEventListener('pointermove', function (e) {
        if (isDragging) seekFromEvent(e.clientX);
      });
      progress.addEventListener('pointerup', function (e) {
        isDragging = false;
        try { progress.releasePointerCapture(e.pointerId); } catch (_) {}
      });
      progress.addEventListener('keydown', function (e) {
        if (!audio.duration) return;
        if (e.key === 'ArrowRight') {
          audio.currentTime = Math.min(audio.duration, audio.currentTime + 5);
          e.preventDefault();
        } else if (e.key === 'ArrowLeft') {
          audio.currentTime = Math.max(0, audio.currentTime - 5);
          e.preventDefault();
        }
      });
    }

    initTracking(root, audio);
  }

  function init() {
    var players = document.querySelectorAll('[data-zh-podcast]');
    players.forEach(initPlayer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
